import { Link } from "react-router-dom";
import { IMAGES, IMAGE_CAPTIONS, SPEAKING_TOPICS } from "../lib/content";
import { ArrowRight, Mic2, Users, Globe } from "lucide-react";
import ZoomableImage from "../components/site/ZoomableImage";

const AUDIENCES = [
  { icon: Users, label: "Universities & Newsrooms" },
  { icon: Globe, label: "International Conferences" },
  { icon: Mic2, label: "Hospitals & Foundations" },
];

export default function Speaking() {
  return (
    <div data-testid="speaking-page" className="bg-cream">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="container-vk pt-20 md:pt-28 pb-20 grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-7">
            <p className="overline mb-6">Speaking</p>
            <h1 className="heading-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight">
              Honest. Specific.{" "}
              <span className="italic text-burgundy">Built for the room</span>.
            </h1>
            <p className="mt-8 text-lg text-ink/70 max-w-2xl leading-relaxed">
              Victor speaks at the intersection of journalism, healthcare,
              migration and climate &mdash; the places where his life has
              already done the reading for the audience.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="speaking-cta-invite"
                className="btn-primary"
              >
                Invite Victor to Speak <ArrowRight size={16} />
              </Link>
              <a
                href="#topics"
                data-testid="speaking-cta-topics"
                className="btn-secondary"
              >
                View Topics
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <ZoomableImage
              src={IMAGES.speakingPodium}
              alt="Victor Kwawukume addressing a public rally with microphone"
              captionOverline="Public Engagement · Accra"
              caption={IMAGE_CAPTIONS.speakingPodium}
              aspect="aspect-[4/5]"
              testId="speaking-hero-image"
            />
          </div>
        </div>
      </section>

      {/* Audiences */}
      <section className="bg-cream-deep py-20" data-testid="speaking-audiences">
        <div className="container-vk grid md:grid-cols-3 gap-6">
          {AUDIENCES.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="bg-cream border border-paperline p-8 flex items-start gap-4"
            >
              <Icon size={22} className="text-burgundy mt-1" />
              <p className="heading-display text-xl md:text-2xl font-medium leading-tight">
                {label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Topics */}
      <section id="topics" className="container-vk py-24 md:py-32" data-testid="speaking-topics">
        <p className="overline mb-5">Talks & Topics</p>
        <h2 className="heading-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.05] max-w-3xl mb-16">
          Six conversations Victor has spent his life preparing for &mdash;
          adaptable to any room.
        </h2>

        <div className="grid md:grid-cols-2 gap-x-12 gap-y-14 stagger">
          {SPEAKING_TOPICS.map((t, i) => (
            <div
              key={t.title}
              data-testid={`topic-${i}`}
              className="animate-fade-up border-t border-paperline pt-8 group"
            >
              <div className="flex items-start gap-6">
                <span className="heading-display text-3xl font-semibold text-burgundy">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="heading-display text-2xl md:text-3xl font-semibold leading-tight">
                    {t.title}
                  </h3>
                  <p className="mt-3 text-ink/75 leading-relaxed">{t.summary}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Booking */}
      <section className="bg-[#0F172A] text-cream py-24 md:py-32" data-testid="speaking-booking">
        <div className="container-vk grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <p className="overline text-gold mb-5">Booking</p>
            <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05]">
              Tell us about your audience &mdash; we&rsquo;ll build the talk for them.
            </h2>
            <p className="mt-6 text-cream/75 max-w-2xl leading-relaxed">
              Keynotes, panels, fireside chats and workshops. Available in-person
              across North America and West Africa, and virtually worldwide.
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link
              to="/contact"
              data-testid="speaking-booking-cta"
              className="inline-flex items-center gap-2 bg-gold text-ink px-7 py-4 font-medium hover:bg-cream transition-colors"
            >
              Send an Inquiry <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
